import React from 'react';
import { AlertTriangle, TrendingUp, Search, ShieldCheck, Heart } from 'lucide-react';

const Mission: React.FC = () => {
  return (
    <section id="why-us" className="py-24 bg-black text-white relative overflow-hidden">
      {/* Modern Background */}
      <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.5'/%3E%3C/svg%3E")` }}></div>
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-orange-600/10 rounded-full blur-[120px] -mr-32 -mt-32 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gray-800/10 rounded-full blur-[120px] -ml-32 -mb-32 pointer-events-none"></div>
      
      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-20">
          <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-orange-900/30 border border-orange-500/30 mb-6 backdrop-blur-md">
            <span className="text-orange-400 text-xs font-bold uppercase tracking-widest">
              Our Mission
            </span>
          </div>
          <h2 className="text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight leading-tight">
            Why We <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-700">Exist</span>
          </h2>
          <p className="text-xl text-gray-400 font-medium max-w-2xl mx-auto leading-relaxed">
            We saw amazing founders getting crushed by a manufacturing industry built on opacity and shortcuts. We decided to fix it.
          </p>
        </div>

        {/* The Problem Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-10 mb-24">
          {/* Card 1 */}
          <div className="group relative bg-gray-900/40 backdrop-blur-md rounded-3xl p-8 lg:p-10 border border-gray-800 hover:border-orange-500/50 transition-all duration-500 overflow-hidden">
             <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity transform group-hover:scale-110 duration-700">
                <AlertTriangle size={120} />
             </div>
             <div className="relative z-10">
                 <div className="w-14 h-14 bg-gray-800 rounded-2xl flex items-center justify-center mb-6 border border-gray-700 group-hover:bg-orange-500 group-hover:text-black transition-all duration-300">
                    <AlertTriangle className="w-7 h-7" strokeWidth={2} />
                 </div>
                 <h3 className="text-2xl font-bold text-white mb-4">The Launch Gap</h3>
                 <p className="text-gray-400 leading-relaxed text-lg">
                   Founders with brilliant ideas were hitting a wall. Manufacturers demanded 5,000 unit MOQs, ignored FSSAI compliance, and offered zero guidance on formulation. Great brands were dying before day one.
                 </p>
             </div>
          </div>

          {/* Card 2 */}
          <div className="group relative bg-gray-900/40 backdrop-blur-md rounded-3xl p-8 lg:p-10 border border-gray-800 hover:border-white/50 transition-all duration-500 overflow-hidden">
             <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity transform group-hover:scale-110 duration-700">
                <TrendingUp size={120} />
             </div>
             <div className="relative z-10">
                 <div className="w-14 h-14 bg-gray-800 rounded-2xl flex items-center justify-center mb-6 border border-gray-700 group-hover:bg-white group-hover:text-black transition-all duration-300">
                    <TrendingUp className="w-7 h-7" strokeWidth={2} />
                 </div>
                 <h3 className="text-2xl font-bold text-white mb-4">The Scaling Trap</h3>
                 <p className="text-gray-400 leading-relaxed text-lg">
                   Scaling brands were stuck in a nightmare of inconsistency. Batch 1 was perfect, Batch 3 clumped. Deadlines were missed. Communication was non-existent. Growth was impossible without trust.
                 </p>
             </div>
          </div>
        </div>

        {/* The Solution / Core Values */}
        <div className="mb-24">
            <div className="text-center mb-12">
                <h3 className="text-3xl font-bold text-white">So we built the <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-700">Anti-Manufacturer</span>.</h3>
                <p className="text-gray-500 mt-4">We replaced the chaos with three non-negotiable pillars.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
                {/* Pillar 1: Transparency */}
                <div className="bg-gradient-to-b from-gray-900 to-black p-8 rounded-3xl border border-gray-800 text-center hover:translate-y-[-5px] transition-transform duration-300">
                    <div className="mx-auto w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mb-6 border border-blue-500/20">
                        <Search className="w-8 h-8 text-blue-500" />
                    </div>
                    <h4 className="text-xl font-bold text-white mb-3">Radical Transparency</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">
                        No hidden fees. No "mystery" delays. You get clear quotes, real-time production updates, and honest timelines. We communicate proactively, not reactively.
                    </p>
                </div>

                {/* Pillar 2: Quality */}
                <div className="bg-gradient-to-b from-gray-900 to-black p-8 rounded-3xl border border-gray-800 text-center hover:translate-y-[-5px] transition-transform duration-300 relative">
                     <div className="absolute inset-0 bg-orange-500/5 rounded-3xl pointer-events-none border border-orange-500/20"></div>
                    <div className="mx-auto w-16 h-16 bg-orange-500/10 rounded-full flex items-center justify-center mb-6 border border-orange-500/20">
                        <ShieldCheck className="w-8 h-8 text-orange-500" />
                    </div>
                    <h4 className="text-xl font-bold text-white mb-3">Obsessive Quality</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">
                        FSSAI, GMP, and ISO are just the baseline. We test raw materials, we test during production, and we test finished goods. If it's not perfect, it doesn't leave the factory.
                    </p>
                </div>

                {/* Pillar 3: Care */}
                <div className="bg-gradient-to-b from-gray-900 to-black p-8 rounded-3xl border border-gray-800 text-center hover:translate-y-[-5px] transition-transform duration-300">
                    <div className="mx-auto w-16 h-16 bg-pink-500/10 rounded-full flex items-center justify-center mb-6 border border-pink-500/20">
                        <Heart className="w-8 h-8 text-pink-500" />
                    </div>
                    <h4 className="text-xl font-bold text-white mb-3">Founder Care</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">
                        We aren't just a factory; we are your partners. We help with formulation, compliance, and strategy. Your success is our business model.
                    </p>
                </div>
            </div>
        </div>

        {/* Stats Section - Dashboard Style */}
        <div className="relative rounded-3xl bg-gray-900 border border-gray-800 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-orange-600/10 to-transparent opacity-50"></div>
            <div className="relative z-10 grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-800">
                <div className="p-8 text-center">
                    <div className="text-4xl md:text-5xl font-black text-white mb-2 tracking-tight">120+</div>
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">Brands Launched</div>
                </div>
                <div className="p-8 text-center">
                    <div className="text-4xl md:text-5xl font-black text-white mb-2 tracking-tight">10M+</div>
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">Units Produced</div>
                </div>
                <div className="p-8 text-center">
                    <div className="text-4xl md:text-5xl font-black text-white mb-2 tracking-tight">500+</div>
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">Formulations</div>
                </div>
                <div className="p-8 text-center bg-orange-600/10">
                    <div className="text-4xl md:text-5xl font-black text-orange-500 mb-2 tracking-tight">99%</div>
                    <div className="text-xs font-bold text-orange-400 uppercase tracking-widest">On-Time Delivery</div>
                </div>
            </div>
        </div>

      </div>
    </section>
  );
};

export default Mission;